import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, profileAPI, presenceAPI, notificationsAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check for existing session
    const storedToken = localStorage.getItem('smbc_token');
    const storedUser = localStorage.getItem('smbc_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token: newToken, user: userData } = response;
      
      localStorage.setItem('smbc_token', newToken);
      localStorage.setItem('smbc_user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token: newToken, user: newUser } = response;
      
      localStorage.setItem('smbc_token', newToken);
      localStorage.setItem('smbc_user', JSON.stringify(newUser));
      setToken(newToken);
      setUser(newUser);
      return newUser;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('smbc_token');
    localStorage.removeItem('smbc_user');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (updates) => {
    try {
      const updatedUser = await profileAPI.update(updates);
      localStorage.setItem('smbc_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const isAdmin = user?.role === 'pastor' || user?.role === 'admin';

  // Presence heartbeat + app icon badge sync while the app is open
  useEffect(() => {
    if (!token) return;

    const syncBadge = async () => {
      try {
        const data = await notificationsAPI.getUnreadCount();
        const total = data?.total || 0;
        if ('setAppBadge' in navigator) {
          if (total > 0) {
            await navigator.setAppBadge(total);
          } else {
            await navigator.clearAppBadge();
          }
        }
      } catch (e) { /* ignore */ }
    };

    const tick = () => {
      presenceAPI.heartbeat().catch(() => {});
      syncBadge();
    };

    tick();
    const interval = setInterval(tick, 30000); // every 30s
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateProfile,
      isAdmin,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
